import { z } from "zod";

const textSchema = z.string().trim().min(1);
const nullableTextSchema = textSchema.nullable();
const countryCodeSchema = z.string().regex(/^[A-Z]{2}$/);
const subdivisionCodeSchema = z.string().regex(/^[A-Z]{2,3}$/);

export const listingPageSchemaVersion = "listing-page.v1" as const;

export const listingPageMoneyRangeSchema = z
    .object({
        currency: z
            .string()
            .trim()
            .regex(/^[A-Z]{3}$/),
        min: z.number().nonnegative().nullable(),
        max: z.number().nonnegative().nullable(),
    })
    .strict()
    .superRefine((range, context) => {
        if (range.min === null && range.max === null) {
            context.addIssue({
                code: "custom",
                message: "A money range requires a minimum or maximum amount.",
            });
        }
        if (range.min !== null && range.max !== null && range.min > range.max) {
            context.addIssue({
                code: "custom",
                message:
                    "A money range minimum cannot be greater than its maximum.",
            });
        }
    });

export const listingPageLocationSchema = z
    .object({
        displayName: textSchema,
        city: nullableTextSchema,
        regionCode: subdivisionCodeSchema.nullable(),
        countryCode: countryCodeSchema.nullable(),
        latitude: z.number().min(-90).max(90).nullable(),
        longitude: z.number().min(-180).max(180).nullable(),
    })
    .strict();

export const listingPageTransportLocationSchema = listingPageLocationSchema
    .extend({
        airportCode: nullableTextSchema,
        kind: z.enum([
            "address",
            "city",
            "state",
            "country",
            "airport",
            "service_area",
            "meetup_point",
            "other",
        ]),
        postalCode: nullableTextSchema,
    })
    .strict();

export const listingPageImageSchema = z
    .object({
        id: textSchema,
        url: z.url(),
        altText: nullableTextSchema,
    })
    .strict();

export const listingPageFactSchema = z
    .object({
        key: textSchema,
        label: textSchema,
        value: textSchema,
    })
    .strict();

export const listingPageScreeningFactSchema = z
    .object({
        key: textSchema,
        label: textSchema,
        value: z.boolean(),
    })
    .strict();

export const listingPageSchema = z
    .object({
        schemaVersion: z.literal(listingPageSchemaVersion),

        listing: z
            .object({
                id: textSchema,
                title: textSchema,
                description: nullableTextSchema,
                descriptionQa: z
                    .array(
                        z
                            .object({
                                question: z.string(),
                                answer: z.string(),
                            })
                            .strict(),
                    )
                    .optional(),
                images: z.array(listingPageImageSchema),
                price: listingPageMoneyRangeSchema.nullable(),
                location: listingPageLocationSchema.nullable(),
                facts: z
                    .object({
                        age: nullableTextSchema,
                        sex: z
                            .enum(["male", "female", "mixed", "unknown"])
                            .nullable(),
                        size: nullableTextSchema,
                        getsAlongWith: z.array(
                            z
                                .object({
                                    type: z.enum([
                                        "children",
                                        "dogs",
                                        "cats",
                                        "other_animals",
                                    ]),
                                    value: z.enum(["yes", "no", "unknown"]),
                                    note: nullableTextSchema,
                                })
                                .strict(),
                        ),
                    })
                    .strict(),
                healthAndWellness: z
                    .object({
                        tests: z.array(
                            z
                                .object({
                                    key: textSchema,
                                    name: textSchema,
                                    result: nullableTextSchema,
                                })
                                .strict(),
                        ),
                        reportedHealth: z.array(
                            z
                                .object({
                                    key: textSchema,
                                    name: textSchema,
                                })
                                .strict(),
                        ),
                        completedCare: z.array(
                            z
                                .object({
                                    key: textSchema,
                                    name: textSchema,
                                })
                                .strict(),
                        ),
                    })
                    .strict()
                    .optional(),
            })
            .strict(),

        breed: z
            .object({
                id: nullableTextSchema,
                name: textSchema,
                description: nullableTextSchema,
                facts: z.array(listingPageFactSchema),
                estimatedPrice: listingPageMoneyRangeSchema.nullable(),
            })
            .strict()
            .nullable(),

        transport: z
            .object({
                options: z.array(
                    z
                        .object({
                            method: textSchema,
                            type: z.enum([
                                "pickup",
                                "meetup",
                                "ground_delivery",
                                "flight_nanny",
                                "air_cargo",
                                "other",
                            ]),
                            cost: listingPageMoneyRangeSchema.nullable(),
                            details: nullableTextSchema,
                            locations: z.array(
                                listingPageTransportLocationSchema,
                            ),
                        })
                        .strict(),
                ),
            })
            .strict()
            .nullable(),

        application: z
            .object({
                url: z.url().optional(),
                requirements: z.array(textSchema),
                screening: z.array(listingPageScreeningFactSchema),
                included: z.array(textSchema),
                deposit: listingPageMoneyRangeSchema.optional(),
            })
            .strict()
            .nullable(),
    })
    .strict();

export const listingPageJsonSchema = z.toJSONSchema(listingPageSchema);

export type ListingPage = z.infer<typeof listingPageSchema>;
